import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebase.js';
import { doc, setDoc, onSnapshot, collection } from 'firebase/firestore';

const SubscriptionContext = createContext({});

export const useSubscription = () => useContext(SubscriptionContext);

const DEFAULT_FEATURES = [
    { id: 'cover_letter', name: 'Cover Letter', price: 1, isActive: true, description: 'AI-generated cover letters' },
    { id: 'roast', name: 'Resume Roast', price: 1, isActive: true, description: 'Brutal AI critique' },
    { id: 'polish_resume', name: 'Polish Resume', price: 2, isActive: true, description: 'Enhance entire resume with AI' },
    { id: 'premium_templates', name: 'Premium Templates', price: 1, isActive: true, description: 'Unlock all premium designs' },
    { id: 'job_analyzer', name: 'Job Analyzer', price: 5, isActive: true, description: 'Analyze job descriptions with AI' },
    { id: 'remove_branding', name: 'Remove Watermark', price: 5, isActive: true, description: 'Remove branding from PDFs' },
    { id: 'linkedin_optimizer', name: 'LinkedIn Optimizer', price: 3, isActive: true, description: 'Viral headlines & bio generator' },
    { id: 'mock_interview', name: 'Mock Interview', price: 5, isActive: true, description: 'AI interviewer with 6 personas, scoring & competency profile' },
    { id: 'salary_negotiation', name: 'Salary Negotiator', price: 3, isActive: true, description: 'Recruiter scripts, counter-offer ladder, red flags' },
    { id: 'all_access', name: 'All Access', price: 10, isActive: true, description: 'Unlock everything forever' }
];

export const SubscriptionProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [unlockedFeatures, setUnlockedFeatures] = useState({});
    const [loading, setLoading] = useState(true);

    // Dynamic Feature Registry
    const [availableFeatures, setAvailableFeatures] = useState([]);
    const [featurePrices, setFeaturePrices] = useState({});

    // 1. Fetch Feature Configuration from Firestore (Real-time)
    useEffect(() => {
        const featuresRef = collection(db, 'features');

        const unsubscribe = onSnapshot(featuresRef, (snapshot) => {
            console.log("Features snapshot update:", snapshot.size, "documents. Empty?", snapshot.empty);

            if (snapshot.empty) {
                // Seed defaults if empty
                console.log("Seeding default features...", DEFAULT_FEATURES);
                Promise.all(DEFAULT_FEATURES.map(f =>
                    setDoc(doc(db, 'features', f.id), f).then(() => console.log(`Seeded ${f.id}`))
                )).then(() => console.log("Seeding complete."))
                    .catch(e => console.error("Seeding failed:", e));
            } else {
                const features = [];
                const prices = {};
                snapshot.forEach(doc => {
                    const data = doc.data();
                    // Merge ID into object
                    features.push({ id: doc.id, ...data });
                    prices[doc.id] = data.price;
                });
                setAvailableFeatures(features);
                setFeaturePrices(prices);
            }
        }, (err) => {
            console.error("Failed to fetch features:", err);
        });

        return () => unsubscribe();
    }, []);

    // 2. Fetch User Subscription (Real-time)
    const isPremium = unlockedFeatures['all_access'] === true;

    useEffect(() => {
        if (!currentUser) {
            setUnlockedFeatures({});
            setLoading(false);
            return;
        }

        const userRef = doc(db, 'users', currentUser.uid);
        const unsubscribe = onSnapshot(userRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                let features = data.unlockedFeatures || {};

                // Legacy migration
                if (data.isPremium && !features.all_access) {
                    features = { ...features, all_access: true, cover_letter: true, roast: true, polish_resume: true };
                }
                setUnlockedFeatures(features);
            } else {
                setUnlockedFeatures({});
            }
            setLoading(false);
        }, (error) => {
            console.error("Error listening to subscription:", error);
            setLoading(false);
        });

        // Check payment return URL
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('payment_status') === 'success') {
            window.history.replaceState({}, document.title, window.location.pathname);
            alert("Payment Successful! Feature will unlock shortly.");
        }

        return () => unsubscribe();
    }, [currentUser]);

    // Helper: Check Access
    const hasFeature = (featureId) => {
        if (!featureId) return false;
        return unlockedFeatures[featureId] === true || unlockedFeatures['all_access'] === true;
    };

    // Helper: Get Price
    const getFeaturePrice = (featureId) => {
        return featurePrices[featureId] !== undefined ? featurePrices[featureId] : 0;
    };

    // Helper: Update Config (Admin)
    // Helper: Update Config (Admin)
    const updateFeatureConfig = async (feature, adminSecret) => {
        if (!feature.id) throw new Error("Feature ID missing");
        if (!adminSecret) throw new Error("Admin Secret required");

        const response = await fetch('/api/admin-features', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'update',
                feature: feature,
                secret: adminSecret
            }),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || "Update failed");
        }
    };

    // Helper: Delete Feature (Admin)
    // Helper: Delete Feature (Admin)
    const deleteFeatureConfig = async (featureId, adminSecret) => {
        if (!featureId) throw new Error("Feature ID missing");
        if (!adminSecret) throw new Error("Admin Secret required");

        const response = await fetch('/api/admin-features', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'delete',
                feature: featureId,
                secret: adminSecret
            }),
        });

        if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || "Delete failed");
        }
    };

    // Action: Purchase
    const purchaseFeature = async (featureId, overridePrice = null) => {
        if (!currentUser) return;
        setLoading(true);

        // Prefer database price, fallback to override
        const price = featurePrices[featureId] !== undefined ? featurePrices[featureId] : overridePrice;

        if (price === null || price === undefined) {
            alert("Error: Price not found for this feature.");
            setLoading(false);
            return;
        }

        // Logic for Free Features: Instant Unlock
        if (price <= 0) {
            console.log(`Feature ${featureId} is free. Unlocking immediately.`);
            await debugUnlockFeature(featureId);
            // Optionally reload or just update local state (debugUnlockFeature already does local state + firestore)
            setLoading(false);
            alert("Feature Unlocked! (Free)");
            return;
        }

        try {
            console.log(`Purchasing ${featureId} for $${price}`);
            const response = await fetch('/api/create-payment', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    uid: currentUser.uid,
                    amount: price,
                    feature: featureId
                }),
            });

            if (!response.ok) {
                const text = await response.text();
                throw new Error(`API Error: ${response.status} - ${text}`);
            }

            const { payLink } = await response.json();
            if (payLink) {
                window.location.href = payLink;
            } else {
                throw new Error("No Payment Link returned.");
            }
        } catch (error) {
            console.error("Purchase failed:", error);
            alert(`Failed to start payment: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Deprecated but kept for UI calls
    const upgradeToPro = () => purchaseFeature('all_access');

    // Debug
    const debugUnlockFeature = async (featureId) => {
        if (currentUser) {
            const newFeatures = { ...unlockedFeatures, [featureId]: true };
            setUnlockedFeatures(newFeatures);
            await setDoc(doc(db, 'users', currentUser.uid), {
                unlockedFeatures: newFeatures
            }, { merge: true });
        }
    };

    return (
        <SubscriptionContext.Provider value={{
            isPremium,
            unlockedFeatures,
            hasFeature,
            purchaseFeature,
            isLoading: loading,
            upgradeToPro,
            debugUnlockFeature,
            // New Exports
            availableFeatures,     // List of features
            getFeaturePrice,       // Get price by ID
            updateFeatureConfig,   // Admin update
            deleteFeatureConfig,   // Admin delete
        }}>
            {children}
        </SubscriptionContext.Provider>
    );
};
